import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Download, EllipsisVertical, Monitor, PlusSquare, Share, Smartphone, Zap, ShieldCheck, Maximize } from 'lucide-react';
import InstallQrCode from '../components/InstallQrCode';
import {
  clearInstallPrompt,
  detectPlatform,
  getInstallPrompt,
  getInstallUrl,
  isStandalone,
  subscribeInstallPrompt,
} from '../lib/pwa';

type InstallState = 'idle' | 'installing' | 'installed';

function Step({ number, icon: Icon, children }: { number: number; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-4 rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-100">
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#0f1b2d] text-sm font-semibold text-white">
        {number}
      </span>
      <div className="flex-1 pt-1 text-[15px] leading-relaxed text-gray-700">{children}</div>
      <Icon className="mt-1 h-5 w-5 flex-shrink-0 text-amber-600" />
    </li>
  );
}

function PrimaryButton({ onClick, disabled, icon: Icon, children }: { onClick: () => void; disabled?: boolean; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0f1b2d] px-6 py-4 text-base font-semibold text-white shadow-lg shadow-slate-900/20 transition-all hover:bg-[#1a2b45] active:scale-[0.98] disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-300"
    >
      <Icon className="h-5 w-5" />
      {children}
    </button>
  );
}

export default function InstallApp() {
  const navigate = useNavigate();
  const platform = detectPlatform();
  const installPrompt = useSyncExternalStore(subscribeInstallPrompt, getInstallPrompt);
  const [standalone] = useState(isStandalone);
  const [state, setState] = useState<InstallState>('idle');

  useEffect(() => {
    const onInstalled = () => setState('installed');
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  const openApp = () => navigate('/login');

  const handleInstall = async () => {
    if (!installPrompt) return;
    setState('installing');
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      clearInstallPrompt();
      setState(outcome === 'accepted' ? 'installed' : 'idle');
    } catch {
      setState('idle');
    }
  };

  const renderContent = () => {
    if (standalone) {
      return (
        <div className="space-y-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Você já está no app</h2>
            <p className="mt-2 text-gray-500">O Cifrão está instalado neste aparelho.</p>
          </div>
          <PrimaryButton onClick={openApp} icon={Smartphone}>Abrir o Cifrão</PrimaryButton>
        </div>
      );
    }

    if (state === 'installed') {
      return (
        <div className="space-y-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">App instalado</h2>
            <p className="mt-2 text-gray-500">Procure o ícone <strong className="font-semibold text-gray-800">Cifrão</strong> na tela inicial do seu celular e entre com seu login de sempre.</p>
          </div>
          <button onClick={openApp} className="text-sm font-semibold text-[#0f1b2d] underline-offset-4 hover:underline">
            Continuar no navegador
          </button>
        </div>
      );
    }

    if (platform === 'ios') {
      return (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold text-gray-900">Instale em 3 passos</h2>
          <ol className="space-y-3">
            <Step number={1} icon={Share}>
              Toque no botão <strong className="font-semibold text-gray-900">Compartilhar</strong> na barra do navegador.
            </Step>
            <Step number={2} icon={PlusSquare}>
              Role as opções e toque em <strong className="font-semibold text-gray-900">Adicionar à Tela de Início</strong>.
            </Step>
            <Step number={3} icon={CheckCircle2}>
              Confirme tocando em <strong className="font-semibold text-gray-900">Adicionar</strong>. Pronto!
            </Step>
          </ol>
          <p className="text-center text-sm text-gray-500">Não encontrou a opção? Abra este link no <strong className="font-semibold text-gray-700">Safari</strong>.</p>
        </div>
      );
    }

    if (platform === 'android') {
      if (installPrompt) {
        return (
          <div className="space-y-4 text-center">
            <PrimaryButton onClick={handleInstall} disabled={state === 'installing'} icon={Download}>
              {state === 'installing' ? 'Instalando...' : 'Instalar app'}
            </PrimaryButton>
            <p className="text-sm text-gray-500">Gratuito e leve. Não ocupa espaço como os apps de loja.</p>
          </div>
        );
      }
      return (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold text-gray-900">Instale em 2 passos</h2>
          <ol className="space-y-3">
            <Step number={1} icon={EllipsisVertical}>
              Toque no menu <strong className="font-semibold text-gray-900">⋮</strong> no canto superior do Chrome.
            </Step>
            <Step number={2} icon={Download}>
              Toque em <strong className="font-semibold text-gray-900">Instalar app</strong> ou <strong className="font-semibold text-gray-900">Adicionar à tela inicial</strong>.
            </Step>
          </ol>
          <p className="text-center text-sm text-gray-500">Se o Cifrão já estiver instalado, abra pelo ícone na tela inicial.</p>
        </div>
      );
    }

    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto w-fit rounded-3xl bg-white p-3 shadow-md ring-1 ring-gray-100">
          <InstallQrCode value={getInstallUrl()} size={200} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Abra no seu celular</h2>
          <p className="mt-1 text-gray-500">Aponte a câmera do celular para o QR Code para instalar o Cifrão.</p>
        </div>
        {installPrompt && (
          <button
            onClick={handleInstall}
            disabled={state === 'installing'}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0f1b2d] underline-offset-4 hover:underline disabled:opacity-60"
          >
            <Monitor className="h-4 w-4" />
            Instalar neste computador
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f5f6f8]">
      <div className="absolute inset-x-0 top-0 h-[340px] bg-gradient-to-b from-[#0f1b2d] via-[#16263d] to-[#1d3150]" />
      <div className="absolute left-1/2 top-[-120px] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-amber-500/20 blur-3xl" />

      <main
        className="relative mx-auto flex min-h-screen max-w-md flex-col px-5"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 48px)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 32px)' }}
      >
        <header className="flex flex-col items-center text-center text-white">
          <img
            src="/icons/icon-192.png"
            alt="Cifrão"
            className="h-24 w-24 rounded-[28px] shadow-2xl shadow-black/30 ring-4 ring-white/10"
          />
          <h1 className="mt-5 text-3xl font-bold tracking-tight">Cifrão</h1>
          <p className="mt-1 text-sm text-slate-300">Gestão inteligente. Resultados reais.</p>
        </header>

        <section className="mt-8 rounded-3xl bg-white p-6 shadow-xl shadow-slate-900/10 ring-1 ring-gray-100">
          {renderContent()}
        </section>

        <ul className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[
            { icon: Zap, label: 'Abre mais rápido' },
            { icon: Maximize, label: 'Tela cheia' },
            { icon: ShieldCheck, label: 'Dados sempre atualizados' },
          ].map(({ icon: Icon, label }) => (
            <li key={label} className="flex flex-col items-center gap-2 rounded-2xl bg-white/70 px-2 py-3 ring-1 ring-gray-100">
              <Icon className="h-5 w-5 text-amber-600" />
              <span className="text-xs font-medium leading-tight text-gray-600">{label}</span>
            </li>
          ))}
        </ul>

        <p className="mt-auto pt-8 text-center text-xs text-gray-400">
          Depois de instalado, entre com seu login de sempre.
        </p>
      </main>
    </div>
  );
}
